/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import ButtonGroup from '../../../../client/js/components/form/ButtonGroup'
import FormField from '../../../../client/js/components/form/fields/FormField'
import FormSaveButton from '../../../../client/js/components/form/buttons/FormSaveButton'
import SmartForm from '../../../../client/js/components/form/SmartForm'
import RecordMetas from '../../../../client/js/components/RecordMetas'
import {
  createFieldInitializer,
  createFieldValidator,
  createFormValidator,
  removeUnknownFields
} from '../../../../client/js/lib/form'
import { AuthContext } from '../../../auth/components/AuthProvider'
import EmailField from '../../../user/components/fields/EmailField'
import { patchCustomer, postCustomer } from '../../lib/rest/customerAPIClient'
import CustomerSchema from '../../schemas/CustomerSchema.mjs'
import AddressField from '../fields/AddressField'
import BirthDateField from '../fields/BirthDateField'
import BirthPlaceField from '../fields/BirthPlaceField'
import CityField from '../fields/CityField'
import EmployerField from '../fields/EmployerField'
import FamilySituationField from '../fields/FamilySituationField'
import FirstNameField from '../fields/FirstNameField'
import FundingMethodField from '../fields/FundingMethodField'
import GeographicAddressField from '../fields/GeographicAddressField'
import LastNameField from '../fields/LastNameField'
import MobileField from '../fields/MobileField'
import NameField from '../fields/NameField'
import NoteField from '../fields/NoteField'
import PhoneField from '../fields/PhoneField'
import ProfessionField from '../fields/ProfessionField'
import FormSection from '../FormSection'
import NoSensibleInfoHere from '../NoSensibleInfoHere'
import RIBInput from '../inputs/RIBInput'
import { parseBoolean } from '@jalik/form-parser'
import { queryClient } from '../../../../client/js/lib/query'
import { getCustomerListLink } from '../../lib/links/customerLinks'
import FormResetButton from '../../../../client/js/components/form/buttons/FormResetButton'
import { Checkbox, SimpleGrid, Stack } from '@mantine/core'
import useCustomForm from '../../../../client/js/hooks/useCustomForm'

export type CustomerFormProps = {
  initialValues: Partial<Customer>
}

function CustomerForm (props: CustomerFormProps) {
  const intl = useIntl()
  const { initialValues } = props
  const auth = useContext(AuthContext)
  const id = initialValues ? initialValues.id : null
  const editable = auth.hasPermission('write:customer')

  const form = useCustomForm({
    disabled: !editable,
    initialValues: useMemo<Partial<Customer>>(() => ({
      isServiceProvider: false,
      ...initialValues
    }), [initialValues]),
    initializeField: createFieldInitializer(CustomerSchema),
    validate: createFormValidator(CustomerSchema),
    validateField: createFieldValidator(CustomerSchema),
    onSubmit: (submitValues) => {
      const finalValues = removeUnknownFields(submitValues, CustomerSchema)
      return id > 0
        ? patchCustomer(id, finalValues)
        : postCustomer(finalValues)
    },
    onSuccess () {
      queryClient.invalidateQueries({ queryKey: ['customer', id] })
      queryClient.invalidateQueries({ queryKey: ['customers'] })
    }
  })

  return (
    <SmartForm
      context={form}
      redirectUrl={id == null ? getCustomerListLink() : undefined}
    >
      <Stack>
        {initialValues
          ? <RecordMetas metas={initialValues} />
          : null}

        <SimpleGrid cols={{ lg: 2 }}>
          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'identity',
              defaultMessage: 'Identité'
            })}
          >
            <LastNameField />
            <FirstNameField />
            <NameField
              label={intl.formatMessage({
                id: 'name_used_in_lease',
                defaultMessage: 'Nom utilisé dans le contrat de location'
              })}
            />
            <BirthDateField />
            <BirthPlaceField />
          </FormSection>

          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'situation',
              defaultMessage: 'Situation'
            })}
          >
            <ProfessionField />
            <EmployerField />
            <FamilySituationField />
          </FormSection>

          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'contacts',
              defaultMessage: 'Contacts'
            })}
          >
            <MobileField />
            <PhoneField />
            <EmailField />
            <EmailField
              name="email2"
              label={intl.formatMessage({
                id: 'secondary_email',
                defaultMessage: 'Adresse e-mail secondaire'
              })}
            />
          </FormSection>

          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'addresses',
              defaultMessage: 'Adresses'
            })}
          >
            <Stack>
              <CityField />
              <GeographicAddressField />
              <AddressField />
            </Stack>
          </FormSection>

          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'banking_information',
              defaultMessage: 'Informations bancaires'
            })}
          >
            <FundingMethodField />
            <FormField
              name="rib"
              label={intl.formatMessage({
                id: 'rib_account',
                defaultMessage: 'Compte RIB'
              })}
              render={RIBInput}
            />
          </FormSection>

          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'other_information',
              defaultMessage: 'Autres informations'
            })}
          >
            <FormField
              label={intl.formatMessage({
                id: 'service_provider',
                defaultMessage: 'Prestataire'
              })}
              name="isServiceProvider"
              parser={parseBoolean}
              render={Checkbox}
              type="checkbox"
              value="true"
            />
            <NoteField description={<NoSensibleInfoHere />} />
          </FormSection>
        </SimpleGrid>

        {editable
          ? <ButtonGroup>
            <FormResetButton />
            <FormSaveButton />
          </ButtonGroup>
          : null}
      </Stack>
    </SmartForm>
  )
}

export default CustomerForm
